import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, Clock } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import CateringForm from "@/components/sections/CateringForm";

import { BUSINESS } from "@/lib/business-info";

// Same atmospheric hero treatment used elsewhere — keeps editorial pacing intact.
const HERO_BG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/uowmbhf2_24B584D4-D59C-4E4E-97AC-8CB525C35052.PNG";

const CTA_BG =
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=2200&q=85";

const scrollToForm = () =>
    document
        .getElementById("catering-inquiry")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

// ---------------- Hero ----------------
function Hero() {
    return (
        <section
            className="relative bg-charcoal pt-40 md:pt-52 pb-28 md:pb-36 overflow-hidden"
            data-testid="contact-hero"
            aria-label="Contact hero"
        >
            <div aria-hidden className="absolute inset-0 z-0">
                <img
                    src={HERO_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-charcoal/55" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/45 via-charcoal/35 to-charcoal" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_60%,rgba(212,175,55,0.10),rgba(0,0,0,0)_55%)]" />
            </div>

            <div className="empire-container relative z-10">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="empire-eyebrow"
                >
                    <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                    Find Us · 385 Main Street, Poughkeepsie
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="contact-heading"
                    className="mt-8 font-serif text-cream text-[2.6rem] leading-[1.04] tracking-[-0.01em] md:text-[4.4rem] lg:text-[5.2rem] max-w-4xl"
                >
                    There&rsquo;s always{" "}
                    <em className="italic font-normal text-gold">room at the table.</em>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="mt-8 max-w-2xl text-cream/82 text-[15.5px] md:text-[17px] font-light leading-[1.75]"
                >
                    Come by hungry. Send us a note about a gathering. Or just call —
                    Michelle still answers when she&rsquo;s in.
                </motion.p>

                {/* Quick info grid */}
                <motion.ul
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.45 }}
                    className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-cream/[0.07] border-y border-cream/[0.07]"
                >
                    <li className="bg-charcoal-800/50 backdrop-blur-sm p-7 md:p-9 flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <MapPin size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Walk In</div>
                            <p className="mt-2 font-serif text-cream text-[1.15rem] md:text-[1.25rem] tracking-tight leading-snug">
                                {BUSINESS.address.line1}<br />
                                {BUSINESS.address.line2}
                            </p>
                        </div>
                    </li>
                    <li className="bg-charcoal-800/50 backdrop-blur-sm p-7 md:p-9 flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <Phone size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Call</div>
                            <a
                                href={BUSINESS.phoneLink}
                                data-testid="contact-phone"
                                className="mt-2 block font-serif text-cream text-[1.15rem] md:text-[1.25rem] tracking-tight hover:text-gold transition-colors"
                            >
                                {BUSINESS.phoneDisplay}
                            </a>
                            <p className="text-cream/45 text-[12px] font-light mt-1">
                                During open hours.
                            </p>
                        </div>
                    </li>
                    <li className="bg-charcoal-800/50 backdrop-blur-sm p-7 md:p-9 flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <Mail size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Write</div>
                            <a
                                href={BUSINESS.emailLink}
                                data-testid="contact-email"
                                className="mt-2 block font-serif text-cream text-[1rem] tracking-tight hover:text-gold transition-colors md:whitespace-nowrap break-words"
                            >
                                {BUSINESS.email}
                            </a>
                        </div>
                    </li>
                </motion.ul>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className="mt-12 flex flex-col sm:flex-row gap-4"
                >
                    <button
                        type="button"
                        onClick={scrollToForm}
                        data-testid="contact-cta-message"
                        className="empire-btn-primary"
                    >
                        Send Us A Note
                        <ArrowRight size={14} />
                    </button>
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="contact-cta-call"
                        className="empire-btn-secondary"
                    >
                        Call The Kitchen
                    </a>
                </motion.div>
            </div>
        </section>
    );
}

// ---------------- Hours strip (small, grounded — feels like a real restaurant card) ----------------
function HoursStrip() {
    return (
        <section
            data-testid="contact-hours"
            className="bg-charcoal border-y border-cream/[0.08]"
            aria-label="Hours"
        >
            <div className="empire-container py-12 md:py-14">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start md:items-center">
                    <div className="md:col-span-4 flex items-start gap-4">
                        <span className="inline-flex h-9 w-9 items-center justify-center border border-gold/40 text-gold shrink-0">
                            <Clock size={14} />
                        </span>
                        <div>
                            <div className="empire-eyebrow !text-cream/55">Open</div>
                            <p className="mt-2 font-serif text-cream text-[1.4rem] md:text-[1.6rem] tracking-tight">
                                Six days a week.
                            </p>
                            <p className="text-cream/55 text-[12.5px] font-light mt-1">
                                We rest on Sundays.
                            </p>
                        </div>
                    </div>
                    <ul className="md:col-span-8 grid grid-cols-2 md:grid-cols-7 gap-3">
                        {BUSINESS.hours.map((h) => {
                            const closed = h.hours === "Closed";
                            return (
                                <li
                                    key={h.day}
                                    className={`p-3 md:p-4 border ${
                                        closed
                                            ? "border-burgundy/40 text-burgundy"
                                            : "border-cream/[0.08] text-cream"
                                    }`}
                                    data-testid={`hours-${h.day.toLowerCase()}`}
                                >
                                    <div className="empire-eyebrow !text-cream/55 !text-[9px]">
                                        {h.day}
                                    </div>
                                    <div
                                        className={`mt-1 text-[12.5px] md:text-[13px] tracking-tight font-light ${
                                            closed ? "uppercase tracking-[0.18em] text-[10px]" : ""
                                        }`}
                                    >
                                        {closed ? "Closed" : h.hours.replace(" – ", "–")}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </section>
    );
}

// ---------------- Map / Find Us ----------------
function FindUs() {
    const mapsQuery = encodeURIComponent(
        `${BUSINESS.name}, ${BUSINESS.address.line1}, ${BUSINESS.address.line2}`
    );
    const directions = `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`;
    const embed = `https://www.google.com/maps?q=${mapsQuery}&hl=en&z=15&output=embed`;

    return (
        <section
            data-testid="contact-find-us"
            className="relative bg-charcoal-800"
            aria-label="Find us"
        >
            <div className="empire-container py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Find Us
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2rem] md:text-[2.8rem] lg:text-[3.2rem] leading-[1.05] tracking-[-0.01em]">
                            On Main Street,<br />
                            <span className="italic text-gold">a few doors from Cannon.</span>
                        </h2>
                        <p className="mt-7 empire-body max-w-md">
                            Street parking on Main. Pull-up curbside for catering pickup.
                            We&rsquo;re right between Cannon and Bridge — look for the warm
                            light through the window.
                        </p>

                        <a
                            href={directions}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-directions"
                            className="empire-btn-secondary mt-10"
                        >
                            Open In Google Maps
                            <ArrowRight size={14} />
                        </a>
                    </div>

                    <div className="lg:col-span-7 relative">
                        <div className="relative aspect-[5/4] md:aspect-[3/2] overflow-hidden border border-cream/[0.08]">
                            <iframe
                                title="Empire Jamaica Fusion on Google Maps"
                                src={embed}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="absolute inset-0 w-full h-full grayscale-[60%] contrast-[1.05] brightness-[0.85]"
                                data-testid="contact-map-embed"
                                allowFullScreen=""
                            />
                            <div
                                className="pointer-events-none absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(18,18,18,0.15) 0%, rgba(18,18,18,0.05) 50%, rgba(18,18,18,0.45) 100%)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------------- Page-specific final CTA (more grounded) ----------------
function GroundedFinalCTA() {
    return (
        <section
            data-testid="contact-final-cta"
            className="relative overflow-hidden bg-burgundy"
            aria-label="A seat by the window"
        >
            <div aria-hidden className="absolute inset-0 z-0">
                <img
                    src={CTA_BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-burgundy/85" />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/65 via-burgundy/70 to-charcoal/55" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10),rgba(0,0,0,0)_55%)]" />
            </div>

            <div className="relative z-10 empire-container py-28 md:py-40 text-center">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="empire-eyebrow !text-gold"
                >
                    Out Of Many, One Table
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="contact-final-heading"
                    className="mt-6 mx-auto max-w-4xl font-serif text-cream text-[2.4rem] md:text-[3.8rem] lg:text-[4.4rem] leading-[1.05] tracking-[-0.01em]"
                >
                    Save us a seat by the window.<br />
                    <span className="italic text-gold">We&rsquo;ll save you one too.</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                    className="mt-8 max-w-xl mx-auto text-cream/85 text-[15.5px] md:text-[17px] font-light leading-[1.75]"
                >
                    Whether it&rsquo;s a Tuesday after work or a Friday with the
                    whole family — come by, send a note, or call. We&rsquo;ll be
                    here.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.45 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="contact-final-call"
                        className="inline-flex items-center justify-center gap-2 bg-cream text-charcoal px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors duration-300"
                    >
                        Call {BUSINESS.phoneDisplay}
                    </a>
                    <Link
                        to="/private-events"
                        data-testid="contact-final-events"
                        className="inline-flex items-center justify-center gap-2 border border-cream/60 text-cream px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-cream hover:text-charcoal transition-colors duration-300"
                    >
                        Plan A Gathering
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}

// ---------------- Page ----------------
export default function Contact() {
    useEffect(() => {
        document.title = "Contact · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="contact-page" className="overflow-x-hidden">
                <Hero />
                <HoursStrip />
                <CateringForm />
                <FindUs />
                <GroundedFinalCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
